import {
  IconToggleButton,
  ToggleButton
} from '@/components/molecules/toggleButton'
import styled, { css } from 'styled-components'
import { validFor } from '@/theme/validFor'
import { Flex } from '@/components/atoms/Grid'
import Link from 'next/link'

import { FC } from 'react'
export enum TabWidth {
  Auto = 'auto',
  FullWidth = 'fullWidth'
}

type TabProps = {
  title: string
  index?: string
  value?: string
  setValue?: (value: string) => void
  tabWidth?: TabWidth | number | string
  underline?: boolean
  scrollToFaq?: () => void
} & Rest

type IconTabProps = {
  iconElement: JSX.Element
} & TabProps

type LinkIconTabProps = {
  href: string
} & IconTabProps

export const LinkIconTab: FC<LinkIconTabProps> = ({
  title,
  index,
  value = 0,
  setValue,
  tabWidth = TabWidth.Auto,
  underline = true,
  href,
  ...rest
}) => {
  return (
    <StyledFlex tabWidth={tabWidth}>
      <Link passHref href={href}>
        <StyledA>
          <StyledIconToggleButton
            title={title}
            selected={index === value}
            onClick={() => setValue && setValue(index)}
            underline={underline}
            {...rest}
          />
        </StyledA>
      </Link>
    </StyledFlex>
  )
}

export const IconTab: FC<IconTabProps> = ({
  title,
  index,
  value = 0,
  setValue,
  tabWidth = TabWidth.Auto,
  underline = true,
  scrollToFaq,
  ...rest
}) => {
  const scrollAndSetFaq = () => {
    scrollToFaq()
    setValue(index)
  }
  return (
    <StyledFlex tabWidth={tabWidth}>
      <StyledIconToggleButton
        title={title}
        selected={index === value}
        onClick={scrollAndSetFaq}
        underline={underline}
        {...rest}
      />
    </StyledFlex>
  )
}

export const Tab: FC<TabProps> = ({
  title,
  index,
  value = 0,
  setValue,
  tabWidth = TabWidth.Auto,
  underline = true,
  ...rest
}) => {
  return (
    <StyledFlex tabWidth={tabWidth}>
      <StyledToggleButton
        title={title}
        selected={index === value}
        onClick={() => setValue && setValue(index)}
        underline={underline}
        {...rest}
      />
    </StyledFlex>
  )
}

const StyledA = styled.a`
  width: inherit;
  color: inherit;
  text-decoration: none;
  display: inherit;
  flex-grow: 1;
  flex-basis: 0;
`

const StyledFlex = styled(Flex)`
  flex-grow: ${({ tabWidth }) => (tabWidth !== TabWidth.FullWidth ? 0 : 1)};
  flex-basis: ${({ tabWidth }) =>
    tabWidth !== TabWidth.FullWidth ? 'auto' : 0};
  ${({ tabWidth }) =>
    tabWidth === TabWidth.Auto || tabWidth === TabWidth.FullWidth
      ? ''
      : `width: ${tabWidth}`};

  ${validFor.mobile`
    ${({ tabWidth }) => {
      switch (tabWidth) {
        case TabWidth.Auto:
          return ''
        case TabWidth.FullWidth:
          return 'width: 100%;'
        default:
          return `width: ${tabWidth};`
      }
    }}
    ${({ tabWidth }) => (tabWidth === TabWidth.Auto ? '' : 'flex-basis: auto')};
    flex-grow: 0;
  `}
  ${validFor.tablet`
    ${({ tabWidth }) => {
      switch (tabWidth) {
        case TabWidth.Auto:
          return ''
        case TabWidth.FullWidth:
          return 'width: 50%;'
        default:
          return `width: ${tabWidth};`
      }
    }}
    ${({ tabWidth }) => (tabWidth === TabWidth.Auto ? '' : 'flex-basis: auto')};
    flex-grow: 0;
  `}
`

const BaseToggleButton = css`
  margin: 4px;
  flex-grow: 1;
`

const StyledIconToggleButton = styled(IconToggleButton)`
  ${BaseToggleButton}
`

const StyledToggleButton = styled(ToggleButton)`
  ${BaseToggleButton}
`
